import { ReactNode, TdHTMLAttributes, ThHTMLAttributes, TableHTMLAttributes } from 'react';

/** Design `.tbl th`: mono 11px uppercase muted header with the line rule under it. */
export const TH_CLASS =
  'whitespace-nowrap border-b border-line px-2.5 pb-2.5 pt-0 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted';

/** Design `.tbl td`: 12px 10px cell with a hairline rule (none on the last row). */
export const TD_CLASS = 'border-b border-hairline px-2.5 py-3 align-middle';

/**
 * Applied to the `<table>`: drops the rule under the last body row and tints rows on hover
 * (`.tbl tr:last-child td`, `.tbl tbody tr:hover td`). SortableTh headers need `border-b border-line`.
 */
export const TABLE_CLASS =
  'w-full border-collapse text-left [&_tbody_tr:hover_td]:bg-surface-2 [&_tbody_tr:last-child_td]:border-b-0';

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  children: ReactNode;
  /** Minimum table width before the wrapper scrolls horizontally (design `.scroll`). */
  minWidth?: number;
  /** Class for the scroll wrapper. */
  wrapperClassName?: string;
}

/** Design `.tbl` inside an `overflow-x-auto` wrapper. Use `Th`/`Td` (or TH_CLASS/TD_CLASS) for cells. */
export default function Table({ children, minWidth, wrapperClassName = '', className = '', style, ...rest }: TableProps) {
  return (
    <div className={`overflow-x-auto ${wrapperClassName}`}>
      <table {...rest} style={minWidth ? { minWidth, ...style } : style} className={`${TABLE_CLASS} ${className}`}>{children}</table>
    </div>
  );
}

export function Th({ align = 'left', className = '', children, ...rest }: ThHTMLAttributes<HTMLTableCellElement> & { align?: keyof typeof ALIGN }) {
  return <th scope="col" {...rest} className={`${TH_CLASS} ${ALIGN[align]} ${className}`}>{children}</th>;
}

export function Td({ align = 'left', className = '', children, ...rest }: TdHTMLAttributes<HTMLTableCellElement> & { align?: keyof typeof ALIGN }) {
  return <td {...rest} className={`${TD_CLASS} ${ALIGN[align]} ${className}`}>{children}</td>;
}
